import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Save, AlertTriangle, Globe, Check, X } from 'lucide-react';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

// Platform-admin view of the automatically generated SEO. Restaurant owners do
// not manage this themselves — everything is derived from their delivery zones,
// name and cuisine via fixed templates in the backend. An admin can override any
// of the three strings; clearing a field restores the generated value.
export const AdminSeoPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [draft, setDraft] = useState({ seo_title: '', seo_description: '', seo_footer: '' });
  const [saving, setSaving] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await adminAPI.getSeoOverview();
      setRows(data.restaurants || []);
    } catch (e) {
      toast.error('Kon SEO-overzicht niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleRecomputeAll = async () => {
    setRecomputing(true);
    try {
      const { data } = await adminAPI.recomputeSeo();
      toast.success(`SEO opnieuw berekend voor ${data.recomputed} restaurants`);
      await fetchOverview();
    } catch (e) {
      toast.error('Herberekenen mislukt');
    } finally {
      setRecomputing(false);
    }
  };

  const openEditor = (row) => {
    setExpanded(row.id);
    setDraft({
      seo_title: row.override.title || '',
      seo_description: row.override.description || '',
      seo_footer: row.override.footer || '',
    });
  };

  const handleSave = async (restaurantId) => {
    setSaving(true);
    try {
      await adminAPI.setSeoOverride(restaurantId, draft);
      toast.success('Overschrijving opgeslagen');
      setExpanded(null);
      await fetchOverview();
    } catch (e) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const clearOverrides = async (restaurantId) => {
    setSaving(true);
    try {
      await adminAPI.setSeoOverride(restaurantId, { seo_title: '', seo_description: '', seo_footer: '' });
      toast.success('Overschrijvingen gewist — automatische tekst is weer actief');
      setExpanded(null);
      await fetchOverview();
    } catch (e) {
      toast.error('Wissen mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-seo-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">SEO Overzicht</h1>
          <p className="text-gray-400 mt-1">
            Automatisch gegenereerd uit bezorgzones, naam en keukentype. Restaurants beheren dit niet zelf.
          </p>
        </div>
        <button
          onClick={handleRecomputeAll}
          disabled={recomputing}
          data-testid="recompute-seo-btn"
          className="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} />
          Alles herberekenen
        </button>
      </div>

      <div className="space-y-4">
        {rows.map((row) => {
          const hasOverride = !!(row.override.title || row.override.description || row.override.footer);
          const isOpen = expanded === row.id;
          return (
            <div key={row.id} className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-semibold">{row.name}</h3>
                      {hasOverride && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                          handmatig overschreven
                        </span>
                      )}
                      {row.custom_domain_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {row.custom_domain}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-1">
                      keuken: {row.cuisine_type || '— niet ingevuld —'} · bezorggebied:{' '}
                      {row.areas.length ? row.areas.join(', ') : '— geen bezorgzones —'}
                    </p>
                  </div>
                  <button
                    onClick={() => (isOpen ? setExpanded(null) : openEditor(row))}
                    data-testid={`edit-seo-${row.id}`}
                    className="text-sm px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white flex-shrink-0"
                  >
                    {isOpen ? 'Sluiten' : 'Overschrijven'}
                  </button>
                </div>

                {row.unnamed_postcodes?.length > 0 && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Postcode(s) zonder stadsnaam: <strong>{row.unnamed_postcodes.join(', ')}</strong>.
                      Deze verschijnen als nummer in de SEO-tekst. Vul de stad in bij de bezorgzone
                      om dit te verhelpen — mensen zoeken op stadsnaam, niet op postcode.
                    </span>
                  </div>
                )}

                <div className="mt-4 space-y-2 text-sm">
                  <SeoLine label="Titel" value={row.effective.title} overridden={!!row.override.title} />
                  <SeoLine label="Beschrijving" value={row.effective.description} overridden={!!row.override.description} />
                  <SeoLine label="Footer" value={row.effective.footer} overridden={!!row.override.footer} />
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-gray-700 bg-gray-900/50 p-5 space-y-4">
                  <p className="text-xs text-gray-400">
                    Laat een veld leeg om de automatisch gegenereerde tekst te gebruiken.
                  </p>
                  <Field
                    label="Titel"
                    placeholder={row.auto.title || ''}
                    value={draft.seo_title}
                    onChange={(v) => setDraft({ ...draft, seo_title: v })}
                    testId={`seo-title-${row.id}`}
                  />
                  <Field
                    label="Beschrijving"
                    placeholder={row.auto.description || ''}
                    value={draft.seo_description}
                    onChange={(v) => setDraft({ ...draft, seo_description: v })}
                    textarea
                    testId={`seo-description-${row.id}`}
                  />
                  <Field
                    label="Footer"
                    placeholder={row.auto.footer || ''}
                    value={draft.seo_footer}
                    onChange={(v) => setDraft({ ...draft, seo_footer: v })}
                    testId={`seo-footer-${row.id}`}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleSave(row.id)}
                      disabled={saving}
                      data-testid={`save-seo-${row.id}`}
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      <Save className="w-4 h-4" /> Opslaan
                    </button>
                    {hasOverride && (
                      <button
                        onClick={() => clearOverrides(row.id)}
                        disabled={saving}
                        data-testid={`clear-seo-${row.id}`}
                        className="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 px-4 py-2 rounded-lg text-sm"
                      >
                        <X className="w-4 h-4" /> Alle overschrijvingen wissen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SeoLine = ({ label, value, overridden }) => (
  <div className="flex gap-3">
    <span className="text-gray-500 w-24 flex-shrink-0">{label}</span>
    <span className="text-gray-200 break-words min-w-0">
      {value || <em className="text-gray-600">leeg</em>}
      {overridden && <Check className="w-3 h-3 text-amber-400 inline ml-2" title="handmatig ingesteld" />}
    </span>
  </div>
);

const Field = ({ label, value, placeholder, onChange, textarea, testId }) => (
  <div className="space-y-1">
    <label className="text-xs text-gray-400">{label}</label>
    {textarea ? (
      <textarea
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-600"
      />
    ) : (
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-600"
      />
    )}
  </div>
);
